
import Vue from "vue";

import AppSelectSaleLayout from "@/components/SelectSaleLayout.vue";
import { Contact, SaleType } from "@/models/base";
import { SaleConfig } from "@/models/dossier-configs/sale-config";
import { SaleConfig as UserSaleConfig }  from "@/models/user-configs/sale-config";
import { ISelectItem } from "winfakt-vue-components";
import { SaleService } from "@/services/sale-service";
import { SaleLayoutInfo } from "@/utils/sale-layouts/sale-layout-info";
import { AuthService } from "@/services/auth-service";
import { MODULE } from "@/config/modules";
import AppContactSearch from "@/components/contacts/ContactSearch.vue";
import { AllContactsService } from "@/services/all-contacts-service";
import { MailSettings } from "@/models/mail-settings";
import AppCustomLanguageSelect from "@/components/CustomLanguageSelect.vue";
import AppElectronDirectorySelect from "@/components/common/ElectronDirectorySelect.vue";
import { BrowserInfo } from "@/utils/browser";
import { TranslateResult } from "vue-i18n";

export default Vue.extend({
	data(){
		return {
			layouts: [] as SaleLayoutInfo[],
			defaultContact: null as null | Contact,
			defaultContactForNewDocument: null as null | Contact,
			defaultMailSettings: null as null | MailSettings,
			userDefaultPrintLayoutOverrideShown: false
		};
	},
	props: {
		saleType: {
			type: Object as ()=>SaleType,
			required: true
		},
		config: {
			type: Object as ()=>SaleConfig,
			required: true
		},
		userConfig: {
			type: Object as ()=>UserSaleConfig,
			required: true
		},
		electronDefaultDownloadLocation: String
	},
	computed: {
		locale(): string {
			return this.$i18n.locale;
		},
		saleLayoutIdItems(): ISelectItem<number>[] {
			return this.layouts.filter(f=>f.ContactGroupID == this.saleType.ContactGroupID).map(l => {
				return {
					text: l.Name,
					value: l.ID
				};
			});
		},
		hasSignatureModule():boolean {
			return AuthService.hasModule(MODULE.DIGITALE_HANDTEKENING);
		},
		hasCarModule():boolean {
			return AuthService.hasModule(MODULE.WAGENPARKBEHEER_PLUS_AUTOKOSTEN);
		},
		isElectron():boolean {
			return BrowserInfo.hasElectronConfigured();
		},
		dontCopyBackordersToNewYearDisabled():boolean {
			if (this.saleType.ID <= 2) {
				return true;
			}
			if (!this.config.ComputeStock) {
				return true;
			}
			return false;
		},
		dontCopyBackordersToNewYear():boolean {
			if (this.dontCopyBackordersToNewYearDisabled) {
				return false;
			}
			return this.config.DontCopyBackordersToNewBookyear;
		},
		userDefaultPrintLayoutOverrideIcon():string[]{
			if (!this.userDefaultPrintLayoutOverrideShown){
				return ["far", "user"];
			}
			return ["fa", "user"];
		},
		toggleUserDefaultPrintLayoutOverrideTooltip():TranslateResult {
			if (this.userDefaultPrintLayoutOverrideShown) {
				return this.$ct("hide-user-default-print-layout-override");
			}
			return this.$ct("show-user-default-print-layout-override");
		}
	},
	methods: {
		selectDefaultContact(contact:Contact) {
			this.defaultContact = contact;
			this.config.DefaultContactID = this.defaultContact.ID;
		},
		clearDefaultContact(){
			this.defaultContact = null;
			this.config.DefaultContactID = 0;
		},
		selectContactForNewDocument(contact:Contact){
			this.defaultContactForNewDocument = contact;
			this.userConfig.DefaultContactID = contact.ID;
		},
		clearDefaultContactForNewDocument(){
			this.defaultContactForNewDocument = null;
			this.userConfig.DefaultContactID = 0;
		},
		setElectronDefaultDownloadLocation(value:string | null){
			this.$emit("electron-default-download-location", value);
		},
		toggleUserDefaultPrintLayoutOverride(){
			this.userDefaultPrintLayoutOverrideShown = !this.userDefaultPrintLayoutOverrideShown;
			if (!this.userDefaultPrintLayoutOverrideShown) {
				this.userConfig.DefaultPrintLayoutIDs = [];
			}
		}
	},
	async mounted(){
		this.layouts = await SaleService.getSaleLayouts();
		if(this.userConfig.DefaultContactID){
			this.defaultContactForNewDocument = await AllContactsService.getContactByID(this.userConfig.DefaultContactID);
		}
		if (this.config.DefaultContactID){
			this.defaultContact = await AllContactsService.getContactByID(this.config.DefaultContactID);
		}
		this.userDefaultPrintLayoutOverrideShown = this.userConfig.DefaultPrintLayoutIDs.length > 0;
	},
	components: {
		AppSelectSaleLayout,
		AppContactSearch,
		AppCustomLanguageSelect,
		AppElectronDirectorySelect
	},
	i18nEx: {
		componentPrefix: "components.sale-settings"
	}
});
